import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Container,
    Stack,
    Typography,
    TextField,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Grid,
    IconButton,
    Divider,
    CircularProgress,
    CardHeader,
    CardContent,
    Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { productService } from '../../../services/productService';
import { zoneService } from '../../../services/zoneService';

// A destination zone is described by how many cities it covers; a legacy zone by
// how many region pairs route through it. Showing "No region pairs" against a
// destination zone reads as a misconfiguration when it is the normal state.
const zoneHelperText = (zone) => {
    if (zone.is_system) {
        return zone.city_count > 0
            ? `${zone.city_count} cities`
            : 'No cities assigned yet';
    }

    return zone.region_pairs.length > 0
        ? `${zone.region_pairs.length} region pairs`
        : 'No region pairs';
};

const ProductForm = ({ product, onSuccess, onCancel, viewMode = false }) => {
    const { handleApiCallWithLoading, showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [zones, setZones] = useState([]);
    const [zonesLoading, setZonesLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        type: true, // true = Document, false = Non-Document
        tax_calculation_type: 'inclusive', // 'inclusive' or 'exclusive'
        default_weight_from: '',
        default_weight_to: '',
        default_zone_rates: [],
        weight_ranges: [{
            from: '',
            to: '',
            zone_rates: []
        }]
    });
    const [errors, setErrors] = useState({});
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        fetchZones();
        if (product) {
            // Extract and validate weight ranges
            const weightRanges = product.weight_ranges || [];
            
            const formattedWeightRanges = weightRanges.length > 0 
                ? weightRanges.map(range => ({
                    from: range.from?.toString() || '',
                    to: range.to?.toString() || '',
                    zone_rates: Array.isArray(range.zone_rates) 
                        ? range.zone_rates.map(zr => ({
                            zone_id: zr.zone_id,
                            rate: zr.rate?.toString() || ''
                        }))
                        : []
                }))
                : [{
                    from: '',
                    to: '',
                    zone_rates: []
                }];

            // Extract default zone rates
            const defaultZoneRates = product.default_zone_rates || [];
            
            const formattedDefaultZoneRates = defaultZoneRates.map(zr => ({
                zone_id: zr.zone_id,
                rate: zr.rate?.toString() || ''
            }));

            const newFormData = {
                name: product.name || '',
                code: product.code || '',
                description: product.description || '',
                type: product.type ?? true,
                tax_calculation_type: product.tax_calculation_type || 'inclusive',
                default_weight_from: product.default_weight_from?.toString() || '',
                default_weight_to: product.default_weight_to?.toString() || '',
                default_zone_rates: formattedDefaultZoneRates,
                weight_ranges: formattedWeightRanges
            };

            setFormData(newFormData);
        }
    }, [product]);

    const fetchZones = async () => {
        await handleApiCallWithLoading(
            zoneService.getAllZones(),
            setZonesLoading,
            {
                errorMessage: 'Failed to fetch zones. Please refresh the page.',
                showSuccess: false,
                onSuccess: (response) => {
                    // Transform zone data to include region pairs information
                    const transformedZones = response.data.map(zone => ({
                        id: zone.id,
                        name: zone.name,
                        code: zone.code,
                        // Destination zones (SunEx Zone, Tamil Nadu, South, West, North, East) are
                        // defined by the cities in them, not by region pairs.
                        is_system: zone.is_system || false,
                        city_count: zone.city_count,
                        region_pairs: zone.region_pairs || []
                    }));
                    setZones(transformedZones);
                }
            }
        );
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateForm()) {
            return;
        }

        setValidationErrors([]);

        const apiCall = product 
            ? productService.updateProduct(product.id, formData)
            : productService.createProduct(formData);

        await handleApiCallWithLoading(
            apiCall,
            setLoading,
            {
                successMessage: `Product ${product ? 'updated' : 'created'} successfully`,
                errorMessage: `Failed to ${product ? 'update' : 'create'} product`,
                onSuccess: () => {
                    onSuccess();
                },
                onError: (error) => {
                    // Handle validation errors
                    if (error.errors) {
                        const newErrors = {};
                        Object.keys(error.errors).forEach(field => {
                            newErrors[field] = error.errors[field][0];
                        });
                        setErrors(newErrors);
                    }
                }
            }
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDefaultZoneRateChange = (zoneId, value) => {
        setFormData(prev => {
            const newDefaultZoneRates = [...prev.default_zone_rates];
            const existingIndex = newDefaultZoneRates.findIndex(zr => zr.zone_id === zoneId);
            
            if (existingIndex > -1) {
                newDefaultZoneRates[existingIndex].rate = value;
            } else {
                newDefaultZoneRates.push({
                    zone_id: zoneId,
                    rate: value
                });
            }
            
            return {
                ...prev,
                default_zone_rates: newDefaultZoneRates
            };
        });
    };

    const handleWeightRangeChange = (rangeIndex, field, value) => {
        setFormData(prev => ({
            ...prev,
            weight_ranges: prev.weight_ranges.map((range, index) =>
                index === rangeIndex
                    ? { ...range, [field]: value }
                    : range
            )
        }));
    };

    const handleZoneRateChange = (rangeIndex, zoneId, value) => {
        setFormData(prev => {
            const newRanges = [...prev.weight_ranges];
            const weightRange = newRanges[rangeIndex];
            
            // Ensure zone_rates array exists
            if (!Array.isArray(weightRange.zone_rates)) {
                weightRange.zone_rates = [];
            }
            
            // Find existing zone rate or create new one
            const zoneRateIndex = weightRange.zone_rates.findIndex(zr => zr.zone_id === zoneId);
            if (zoneRateIndex > -1) {
                weightRange.zone_rates[zoneRateIndex].rate = value;
            } else {
                weightRange.zone_rates.push({
                    zone_id: zoneId,
                    rate: value
                });
            }
            
            return {
                ...prev,
                weight_ranges: newRanges
            };
        });
    };

    const addWeightRange = () => {
        setFormData(prev => ({
            ...prev,
            weight_ranges: [
                ...prev.weight_ranges,
                {
                    from: '',
                    to: '',
                    zone_rates: []
                }
            ]
        }));
    };

    const removeWeightRange = (rangeIndex) => {
        setFormData(prev => ({
            ...prev,
            weight_ranges: prev.weight_ranges.filter((_, index) => index !== rangeIndex)
        }));
    };

    const validateForm = () => {
        const errors = {};
        const validationErrors = [];

        if (!formData.name) {
            errors.name = "Name is required";
            validationErrors.push("Name is required");
        }
        if (!formData.code) {
            errors.code = "Code is required";
            validationErrors.push("Code is required");
        }
        if (!formData.description) {
            errors.description = "Description is required";
            validationErrors.push("Description is required");
        }
        if (formData.type === null || formData.type === undefined) {
            errors.type = "Product type is required";
            validationErrors.push("Product type is required");
        }
        if (!formData.default_weight_from) {
            errors.default_weight_from = "Default weight range start is required";
            validationErrors.push("Default weight range start is required");
        }
        if (!formData.default_weight_to) {
            errors.default_weight_to = "Default weight range end is required";
            validationErrors.push("Default weight range end is required");
        }
        if (parseFloat(formData.default_weight_to) <= parseFloat(formData.default_weight_from)) {
            errors.default_weight_to = "Default weight range end must be greater than start";
            validationErrors.push("Default weight range end must be greater than start");
        }

        // Validate default zone rates
        if (!formData.default_zone_rates || formData.default_zone_rates.length === 0) {
            errors.default_zone_rates = "At least one default zone rate is required";
            validationErrors.push("At least one default zone rate is required");
        } else {
            formData.default_zone_rates.forEach((zoneRate, index) => {
                if (!zoneRate.rate || parseFloat(zoneRate.rate) <= 0) {
                    errors[`default_zone_rates.${index}.rate`] = "Default zone rate must be greater than 0";
                    validationErrors.push(`Default zone rate for zone ${index + 1} must be greater than 0`);
                }
            });
        }

        setErrors(errors);
        setValidationErrors(validationErrors);

        return Object.keys(errors).length === 0;
    };

    // Ensure default zone rates are initialized for all zones when zones are loaded
    useEffect(() => {
        if (zones.length > 0 && formData.default_zone_rates.length === 0 && product) {
            // Initialize default zone rates for all zones with empty values
            const initializedDefaultZoneRates = zones.map(zone => ({
                zone_id: zone.id,
                rate: ''
            }));
            
            setFormData(prev => ({
                ...prev,
                default_zone_rates: initializedDefaultZoneRates
            }));
        }
    }, [zones, product, formData.default_zone_rates.length]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {validationErrors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Please fix the following issues:</Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {validationErrors.map((error, index) => (
                            <li key={index}>
                                <Typography variant="body2">{error}</Typography>
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Card>
                <CardHeader title="Product Information" />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Code"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                required
                                disabled={viewMode}
                                error={!!errors.code}
                                helperText={errors.code}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                disabled={viewMode}
                                error={!!errors.description}
                                helperText={errors.description}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl>
                                <Typography variant="subtitle2" gutterBottom>
                                    Product Type
                                </Typography>
                                <RadioGroup
                                    name="type"
                                    value={formData.type}
                                    onChange={(e) => handleChange({
                                        target: { name: 'type', value: e.target.value === 'true' }
                                    })}
                                    disabled={viewMode}
                                >
                                    <FormControlLabel
                                        value={true}
                                        control={<Radio disabled={viewMode} />}
                                        label="Document"
                                    />
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio disabled={viewMode} />}
                                        label="Non-Document"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl>
                                <Typography variant="subtitle2" gutterBottom>
                                    Tax Calculation Type
                                </Typography>
                                <RadioGroup
                                    name="tax_calculation_type"
                                    value={formData.tax_calculation_type}
                                    onChange={(e) => handleChange({
                                        target: { name: 'tax_calculation_type', value: e.target.value }
                                    })}
                                    disabled={viewMode}
                                >
                                    <FormControlLabel
                                        value="inclusive"
                                        control={<Radio disabled={viewMode} />}
                                        label="Inclusive"
                                    />
                                     <FormControlLabel
                                        value="exclusive"
                                        control={<Radio disabled={viewMode} />}
                                        label="Exclusive"
                                    />
                                </RadioGroup>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    {formData.tax_calculation_type === 'inclusive' 
                                        ? 'Tax is included in the product price.'
                                        : 'Tax is added to the product price.'
                                    }
                                </Typography>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>Default Rate Configuration</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Configure the default weight range and rates that will apply when the booking weight falls within this range.
                </Typography>
                
                <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Default Weight Range Start (kg)"
                            name="default_weight_from"
                            type="number"
                            value={formData.default_weight_from}
                            onChange={handleChange}
                            required
                            disabled={viewMode}
                            error={!!errors.default_weight_from}
                            helperText={errors.default_weight_from}
                            inputProps={{ min: 0, step: 0.1 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Default Weight Range End (kg)"
                            name="default_weight_to"
                            type="number"
                            value={formData.default_weight_to}
                            onChange={handleChange}
                            required
                            disabled={viewMode}
                            error={!!errors.default_weight_to}
                            helperText={errors.default_weight_to}
                            inputProps={{ min: 0, step: 0.1 }}
                        />
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom>
                    Default Zone Rates
                </Typography>
                <Grid container spacing={2}>
                    {zones.map((zone) => {
                        // Find the rate for this zone
                        const zoneRate = formData.default_zone_rates.find(zr => zr.zone_id === zone.id);
                        const rateValue = zoneRate ? zoneRate.rate : '';
                        
                        return (
                            <Grid item xs={12} sm={6} md={4} key={zone.id}>
                                <TextField
                                    fullWidth
                                    label={`${zone.name} (${zone.code})`}
                                    type="number"
                                    value={rateValue}
                                    onChange={(e) => handleDefaultZoneRateChange(zone.id, e.target.value)}
                                    disabled={viewMode}
                                    required
                                    error={!!errors[`default_zone_rates.${zone.id}.rate`]}
                                    helperText={errors[`default_zone_rates.${zone.id}.rate`] || zoneHelperText(zone)}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Card>

            <Card sx={{ p: 3, mt: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h6">Additional Weight Ranges (Optional)</Typography>
                    {!viewMode && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={addWeightRange}
                        >
                            Add Weight Range
                        </Button>
                    )}
                </Stack>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Configure additional weight ranges for extended pricing. These rates will be added to the default rate when the booking weight exceeds the default range.
                </Typography>

                {formData.weight_ranges.map((range, rangeIndex) => (
                    <Box key={rangeIndex} sx={{ mb: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Typography variant="subtitle1">
                                Additional Weight Range {rangeIndex + 1}
                            </Typography>
                            {!viewMode && (
                                <IconButton
                                    color="error"
                                    onClick={() => removeWeightRange(rangeIndex)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Stack>

                        <Grid container spacing={3} mb={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Weight (kg)"
                                    type="number"
                                    value={range.from}
                                    onChange={(e) => handleWeightRangeChange(rangeIndex, 'from', e.target.value)}
                                    disabled={viewMode}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Weight (kg)"
                                    type="number"
                                    value={range.to}
                                    onChange={(e) => handleWeightRangeChange(rangeIndex, 'to', e.target.value)}
                                    disabled={viewMode}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Additional Zone Rates
                            </Typography>
                            <Grid container spacing={2}>
                                {zones.map((zone) => (
                                    <Grid item xs={12} sm={6} md={4} key={zone.id}>
                                        <TextField
                                            fullWidth
                                            label={`${zone.name} (${zone.code})`}
                                            type="number"
                                            value={range.zone_rates.find(zr => zr.zone_id === zone.id)?.rate || ''}
                                            onChange={(e) => handleZoneRateChange(rangeIndex, zone.id, e.target.value)}
                                            disabled={viewMode}
                                            helperText={zoneHelperText(zone)}
                                            inputProps={{ min: 0, step: 0.01 }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Card>

            {!viewMode && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {product ? 'Update Product' : 'Create Product'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ProductForm; 